// React Query hooks for the user's saved Agents.
// Wraps the `agents` table created in the agents migration.
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

export interface Agent {
  id: string;
  user_id: string;
  name: string;
  description: string | null;
  brain_model: string;
  system_prompt: string;
  tools: string[];
  default_goal: string;
  visibility: string;
  created_at: string;
  updated_at: string;
}

export type AgentDraft = Pick<
  Agent,
  "name" | "description" | "brain_model" | "system_prompt" | "tools" | "default_goal" | "visibility"
>;

const KEY = ["agents", "mine"] as const;

export function useMyAgents() {
  const { user } = useAuth();
  return useQuery({
    queryKey: KEY,
    enabled: !!user,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("agents")
        .select("*")
        .order("updated_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as Agent[];
    },
  });
}

export function useCreateAgent() {
  const qc = useQueryClient();
  const { user } = useAuth();
  return useMutation({
    mutationFn: async (draft: AgentDraft) => {
      if (!user) throw new Error("Not signed in");
      const { data, error } = await supabase
        .from("agents")
        .insert({ ...draft, user_id: user.id })
        .select("*")
        .single();
      if (error) throw error;
      return data as Agent;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useUpdateAgent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, patch }: { id: string; patch: Partial<AgentDraft> }) => {
      const { data, error } = await supabase
        .from("agents")
        .update(patch)
        .eq("id", id)
        .select("*")
        .single();
      if (error) throw error;
      return data as Agent;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useDeleteAgent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("agents").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}